import { cloneDeep, isEmpty, isEqual, pick } from 'lodash';
import { Types } from 'mongoose';
import {
  createLineItem,
  type LineItem,
  LineItemEntity,
  type StoreWithEntity,
  setWithEntity,
  type UserEntity,
  updateLineItem
} from 'sonata-static-storage/lib/entities';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { create } from 'zustand';
import createContext from 'zustand/context';

import { countAdChanges } from '@/helpers/ads/adSaveActions.helper';
import { getCapitalTimeZone, getTimeZonesByCountry } from '@/helpers/countriesOptions/countriesOptions.helper';
import {
  EMPTY_DOOH_AUDIENCE,
  hasDoohAudienceChanges,
  persistDoohAudience
} from '@/helpers/doohAudience/doohAudience.helper';
import {
  CONTEXT_TARGETING_FIELDS,
  CREATIVES_OTHER_FIELDS,
  countChangedFields,
  MAIN_SETTINGS_EXCLUDED_FIELDS
} from '@/helpers/entity/entityFieldChanges.helper';
import { hasRetargetingSegmentToSync } from '@/helpers/targeting/retargetingSegment.helper';
import { EMPTY_SAVE_STEP_COUNTS } from '@/types/saveSteps.types';

import type { LineItemGoal } from 'sonata-static-storage/lib/entities/lineItem/lineItem.types';
import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { AttributionSettings } from 'sonata-static-storage/lib/schemas/campaign/attributionSettings.schema';
import type { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { OptimizationModelInterface } from 'sonata-static-storage/lib/schemas/campaign/optimizationModel.schema';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { SchedulePartingInterface } from 'sonata-static-storage/lib/schemas/campaign/scheduleParting.schema';
import type { ScreenGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';
import type { ZoneGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/zoneGroup.schema';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { InsertionOrder } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrder.schema';
import type { PixelAssociatedEntity } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { DoohAudience } from '@/services/doohAudience.service';
import type { SaveStepCounts, SaveStepStatus, SaveSteps } from '@/types/saveSteps.types';

export type LineItemStore = StoreWithEntity<
  Omit<LineItem, 'type'>,
  LineItemEntity,
  {
    ads: AdInterface<string>[];
    attributionSettings: AttributionSettings;
    contextTargetingCount: number;
    contextualBidding: ContextualBiddingInterface;
    creativesOtherCount: number;
    doohAudience: DoohAudience;
    savedBaseline: Record<string, unknown>;
    savedDoohAudience: DoohAudience;
    editLineItem: (requestConfig?: Omit<RequestInit, 'data' | 'method'>) => Promise<void>;
    isLoading: boolean;
    hasUnsavedMapChanges: boolean;
    mainSettingsCount: number;
    mapSave: () => Promise<void>;
    mapSavePromise: Promise<void> | null;
    mediaType: LineItem['mediaType'];
    pixels: PixelAssociatedEntity[];
    pendingToCreate?: boolean;
    resetMapSave: () => void;
    resolveMapSave: (() => void) | null;
    saveLineItem: (requestConfig?: Record<string, boolean>) => Promise<void>;
    pendingSaveSteps: Pick<SaveSteps, 'venues' | 'attributionZones' | 'screens'>;
    saveSteps: SaveSteps;
    scheduleParting: SchedulePartingInterface;
    screenGroups: ScreenGroupInterface[];
    setAds: (adsData: LineItem['ads']) => void;
    setAdsDistribution: (newState: LineItem['adsDistribution']) => void;
    setAdsPriceEnabled: (newState: LineItem['adsPriceEnabled']) => void;
    setAttributionSettings: (attributionSettingsData: AttributionSettings) => void;
    setBeginDate: (beginDateData: LineItem['beginDate']) => void;
    setCompleted: (completedState: boolean) => void;
    setContextualBidding: (contextualBidding: ContextualBiddingInterface) => void;
    setCountries: (countries: string[]) => void;
    setCustomEvents: (newState: LineItem['customEvents']) => void;
    setDco: (dcoData: LineItem['dco']) => void;
    setDoohAudience: (doohAudience: DoohAudience) => void;
    setEndDate: (endDateData: LineItem['endDate']) => void;
    setGoal: (newGoal: LineItemGoal) => void;
    setLiName: (lineNameData: string) => void;
    setHasUnsavedMapChanges: (hasUnsavedChanges: boolean) => void;
    setMediaType: (mediaType: LineItem['mediaType']) => void;
    setPixels: (pixels: PixelAssociatedEntity[]) => void;
    setPricingModelConfig: (pricingModelConfig: PricingModelConfig) => void;
    setSaveStep: (step: keyof SaveSteps, status: SaveStepStatus, counts?: SaveStepCounts) => void;
    setPendingSaveSteps: (steps: Pick<SaveSteps, 'venues' | 'attributionZones' | 'screens'>) => void;
    setScheduleParting: (schedulePartingData: SchedulePartingInterface) => void;
    setScreenGroups: (screenGroups: ScreenGroupInterface[]) => void;
    setTargeting: (targeting?: TargetingBaseType) => void;
    setTimezone: (timezone: string) => void;
    setOptimizationModel: (optimizationModel?: OptimizationModelInterface) => void;
    setUserFrequencyCapping: (FrequencyCapping: FrequencyCapping) => void;
    setVenues: (venues: Venue<string>[]) => void;
    setZoneGroups: (zoneGroups: ZoneGroupInterface<string>[]) => void;
    shouldMapSave: boolean;
    targeting?: TargetingBaseType;
    type: CampaignFamilyEnum | undefined;
    venues: Venue<string>[];
    zoneGroups: ZoneGroupInterface<string>[];
  }
>;

type CreateStoreOpts = {
  /** The one of the line item, or of its insertion order for a new one (it inherits it on creation) */
  doohAudience?: DoohAudience;
};

const { Provider, useStore } = createContext();

const TRACKED_FIELDS = [
  '_id',
  'ads',
  'adsDistribution',
  'adsPriceEnabled',
  'attributionSettings',
  'beginDate',
  'completed',
  'contextualBidding',
  'countries',
  'customEvents',
  'dco',
  'endDate',
  'goals',
  'mediaType',
  'name',
  'optimizationModel',
  'pixels',
  'pricingModelConfig',
  'scheduleParting',
  'screenGroups',
  'targeting',
  'timezone',
  'type',
  'userFrequencyCapping',
  'zoneGroups'
] as const;

const MAIN_SETTINGS_FIELDS = TRACKED_FIELDS.filter(
  field => !(MAIN_SETTINGS_EXCLUDED_FIELDS as readonly string[]).includes(field)
);

const getHasUnsavedEntityChanges = (store: LineItemStore): boolean =>
  !isEqual(pick(store, TRACKED_FIELDS), store.savedBaseline);

const getHasUnsavedDoohAudienceChanges = (store: LineItemStore): boolean =>
  hasDoohAudienceChanges(store.doohAudience, store.savedDoohAudience);

const getHasUnsavedChanges = (store: LineItemStore): boolean =>
  getHasUnsavedEntityChanges(store) || getHasUnsavedDoohAudienceChanges(store) || store.hasUnsavedMapChanges;

const initialState = (li?: Partial<LineItem>) => {
  const timeZones = getTimeZonesByCountry(li?.countries?.[0] || '');
  const capitalTz = getCapitalTimeZone(li?.countries?.[0] || '');
  const timezone = timeZones.find(tz => tz === capitalTz) || timeZones[0] || li?.timezone || '';
  return {
    ...LineItemEntity.defaultValues,
    ...li,
    _id: li?._id || new Types.ObjectId().toString(),
    countries: li?.countries || [],
    errors: null,
    isLoading: false,
    mediaType: li?.mediaType || 'openRtb',
    name: li?.name || '',
    pendingToCreate: !li?._id,
    pricingModelConfig: li?.pricingModelConfig,
    scheduleParting: li?.scheduleParting || {
      config: {},
      enabled: false,
      yearDate: {
        aggregated: false,
        enabled: false
      }
    },
    screenGroups: li?.screenGroups || [],
    targeting: li?.targeting,
    timezone,
    type: li?.type,
    userFrequencyCapping: li?.userFrequencyCapping,
    zoneGroups: li?.zoneGroups || []
  };
};

const createStore = (
  li: Partial<LineItem> | undefined,
  io: InsertionOrder,
  user: UserEntity,
  { doohAudience = EMPTY_DOOH_AUDIENCE }: CreateStoreOpts = {}
) =>
  create<LineItemStore>(
    // @ts-expect-error
    (set, get) => {
      const newLI = isEmpty(li);
      const entity = new LineItemEntity(initialState(li), user, newLI);
      const insertionOrderId = li?.insertionOrder || io?._id;
      // The stored audience replaces the one of the form: with a text, it holds the variables resolved with AI
      const saveDoohAudience = async (lineItemId: string) => {
        const { doohAudience: current, savedDoohAudience: saved, type } = get();
        const stored = await persistDoohAudience({
          current,
          entity: 'lineItem',
          entityId: lineItemId,
          isDooh: Boolean(type && isDOOHCampaign(type)),
          saved,
          userId: user.get('id')!
        });
        if (stored) {
          set({ doohAudience: stored, savedDoohAudience: stored });
        }
      };

      return {
        ...entity.toJSON(),
        contextTargetingCount: 0,
        creativesOtherCount: 0,
        doohAudience,
        editLineItem: async () => {
          set({ isLoading: true });
          const hasUnsavedEntityChanges = getHasUnsavedEntityChanges(get());
          const hasEntityModelChanges = hasUnsavedEntityChanges || getHasUnsavedDoohAudienceChanges(get());
          const savedBaseline = get().savedBaseline as Record<string, unknown>;
          const creativesCounts = countAdChanges(
            get().ads,
            (savedBaseline as { ads?: AdInterface<string>[] }).ads ?? []
          );
          const mainSettingsCount = countChangedFields(get(), savedBaseline, MAIN_SETTINGS_FIELDS);
          const contextTargetingCount = countChangedFields(get(), savedBaseline, CONTEXT_TARGETING_FIELDS);
          const creativesOtherCount = countChangedFields(get(), savedBaseline, CREATIVES_OTHER_FIELDS);
          // The map provider owns the venues and screens steps: it reports them
          // (with their counts) as soon as `mapSave` reaches it.
          set({
            contextTargetingCount,
            creativesOtherCount,
            mainSettingsCount,
            saveSteps: {
              attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
              entityModel: { counts: creativesCounts, status: hasEntityModelChanges ? 'pending' : 'disabled' },
              screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
              venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' }
            }
          });
          await get().mapSave();
          const mapSteps = get().saveSteps;
          if (
            mapSteps.venues.status === 'fail' ||
            mapSteps.attributionZones.status === 'fail' ||
            mapSteps.screens.status === 'fail'
          ) {
            set({ isLoading: false });
            throw new Error('Map data failed to save, aborting entity update');
          }
          const currentState = get();
          const {
            _id,
            ads,
            adsDistribution,
            adsPriceEnabled,
            attributionSettings,
            beginDate,
            completed,
            contextualBidding,
            countries,
            customEvents,
            dco,
            endDate,
            goals,
            mediaType,
            name,
            optimizationModel,
            pixels,
            pricingModelConfig,
            scheduleParting,
            screenGroups,
            targeting,
            timezone,
            type,
            userFrequencyCapping,
            zoneGroups
          } = currentState;

          const {
            agencyFeeModel,
            bidModels,
            cpalEvent,
            cpvEvent,
            expectedATR,
            expectedCTR,
            expectedETR,
            expectedPVTR,
            expectedSVTR,
            expectedVTR,
            margin,
            marginCPM
          } = pricingModelConfig;
          if (hasEntityModelChanges) {
            get().setSaveStep('entityModel', 'processing');
          }
          try {
            const savedLineItem = await updateLineItem({
              _id,
              ads,
              adsDistribution,
              adsPriceEnabled,
              agencyFeeModel,
              attributionSettings,
              beginDate,
              bidModels,
              completed,
              contextualBidding,
              countries,
              cpalEvent,
              cpvEvent,
              customEvents,
              dco,
              endDate,
              expectedATR,
              expectedCTR,
              expectedETR,
              expectedPVTR,
              expectedSVTR,
              expectedVTR,
              goals,
              insertionOrder: insertionOrderId,
              margin,
              marginCPM,
              mediaType,
              name,
              optimizationModel,
              pixels,
              pricingModelConfig,
              scheduleParting,
              screenGroups,
              targeting,
              timezone,
              type,
              userFrequencyCapping,
              zoneGroups
            });
            if (hasRetargetingSegmentToSync(targeting)) {
              get().setTargeting(savedLineItem.targeting);
            }
            // Only advance the baseline on a real success — resetting it on
            // failure too would make the still-unsaved changes look saved.
            set({ savedBaseline: cloneDeep(pick(get(), TRACKED_FIELDS)) });
            await saveDoohAudience(_id!);
            if (hasEntityModelChanges) {
              get().setSaveStep('entityModel', 'success');
            }
          } catch (error) {
            get().setSaveStep('entityModel', 'fail');
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },
        entity,
        hasUnsavedMapChanges: false,
        insertionOrder: insertionOrderId,
        mainSettingsCount: 0,
        mapSave: () => {
          const existing = get().mapSavePromise;
          if (existing) {
            return existing;
          }
          let resolver!: () => void;
          const promise = new Promise<void>(resolve => {
            resolver = resolve;
          });
          set({ mapSavePromise: promise, resolveMapSave: resolver, shouldMapSave: true });
          return promise;
        },
        mapSavePromise: null,
        pendingSaveSteps: {
          attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'disabled' },
          screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'disabled' },
          venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'disabled' }
        },
        resetMapSave: () => {
          const { resolveMapSave } = get();
          resolveMapSave?.();
          set({ mapSavePromise: null, resolveMapSave: null, shouldMapSave: false });
        },
        resolveMapSave: null,
        saveLineItem: async (requestConfig?: Record<string, boolean>) => {
          const currentState = get();
          if (currentState.pendingToCreate) {
            set({ isLoading: true });
            const savedBaseline = currentState.savedBaseline as Record<string, unknown>;
            const creativesCounts = countAdChanges(
              currentState.ads,
              (savedBaseline as { ads?: AdInterface<string>[] }).ads ?? []
            );
            const mainSettingsCount = countChangedFields(currentState, savedBaseline, MAIN_SETTINGS_FIELDS);
            const contextTargetingCount = countChangedFields(currentState, savedBaseline, CONTEXT_TARGETING_FIELDS);
            const creativesOtherCount = countChangedFields(currentState, savedBaseline, CREATIVES_OTHER_FIELDS);
            // Creating from scratch: the entity always saves, and the map
            // provider reports its own two steps once `mapSave` runs below.
            set({
              contextTargetingCount,
              creativesOtherCount,
              mainSettingsCount,
              saveSteps: {
                attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
                entityModel: { counts: creativesCounts, status: 'pending' },
                screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
                venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' }
              }
            });
            const {
              _id,
              ads,
              adsDistribution,
              adsPriceEnabled,
              attributionSettings,
              beginDate,
              completed,
              contextualBidding,
              countries,
              customEvents,
              dco,
              endDate,
              goals,
              mediaType,
              name,
              optimizationModel,
              pixels,
              pricingModelConfig,
              scheduleParting,
              screenGroups,
              targeting,
              timezone,
              type,
              userFrequencyCapping,
              zoneGroups
            } = currentState;
            const {
              agencyFeeModel,
              bidModels,
              cpalEvent,
              cpvEvent,
              expectedATR,
              expectedCTR,
              expectedETR,
              expectedPVTR,
              expectedSVTR,
              expectedVTR,
              margin,
              marginCPM
            } = pricingModelConfig;
            let newLineItem: LineItemEntity;
            get().setSaveStep('entityModel', 'processing');
            try {
              newLineItem = (await createLineItem(
                {
                  _id,
                  ads,
                  adsDistribution,
                  adsPriceEnabled,
                  agencyFeeModel,
                  attributionSettings,
                  beginDate,
                  bidModels,
                  completed,
                  contextualBidding,
                  countries,
                  cpalEvent,
                  cpvEvent,
                  customEvents,
                  dco,
                  endDate,
                  expectedATR,
                  expectedCTR,
                  expectedETR,
                  expectedPVTR,
                  expectedSVTR,
                  expectedVTR,
                  goals,
                  insertionOrder: insertionOrderId,
                  margin,
                  marginCPM,
                  mediaType,
                  name,
                  optimizationModel,
                  pixels,
                  pricingModelConfig,
                  scheduleParting,
                  screenGroups,
                  targeting,
                  timezone,
                  type,
                  userFrequencyCapping,
                  zoneGroups
                },
                requestConfig
              )) as LineItemEntity;
              get().setSaveStep('entityModel', 'success');
            } catch (error) {
              set({ isLoading: false });
              get().setSaveStep('entityModel', 'fail');
              throw error;
            }
            if (hasRetargetingSegmentToSync(targeting)) {
              get().setTargeting(newLineItem.entity.targeting);
            }
            // Mutate the existing entity in place rather than replacing it —
            // setAds/setAdsDistribution/etc. were bound to this entity instance
            // at store-creation time, so swapping in a new instance here would
            // silently detach them from what the UI renders.
            set(state => {
              state.entity.set('_id', newLineItem.entity._id);
              return {
                entity: state.entity,
                isLoading: false,
                pendingToCreate: false
              };
            });
            set({ savedBaseline: cloneDeep(pick(get(), TRACKED_FIELDS)) });
            // A new line item has to exist before its audience can be saved (it has inherited the one of its IO)
            try {
              await saveDoohAudience(newLineItem.entity._id || _id!);
            } catch (error) {
              get().setSaveStep('entityModel', 'fail');
              throw error;
            }
            if (newLineItem.entity._id) {
              await get().mapSave();
            }
          }
        },
        saveSteps: {
          attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
          entityModel: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
          screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
          venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' }
        },
        savedBaseline: cloneDeep(pick(entity.toJSON(), TRACKED_FIELDS)),
        savedDoohAudience: doohAudience,
        setAds: setWithEntity<LineItemEntity>('ads', entity, set),
        setAdsDistribution: setWithEntity<LineItemEntity>('adsDistribution', entity, set),
        setAdsPriceEnabled: setWithEntity<LineItemEntity>('adsPriceEnabled', entity, set),
        setAttributionSettings: setWithEntity<LineItemEntity>('attributionSettings', entity, set),
        setBeginDate: setWithEntity<LineItemEntity>('beginDate', entity, set),
        setCompleted: setWithEntity<LineItemEntity>('completed', entity, set),
        setContextualBidding: setWithEntity<LineItemEntity>('contextualBidding', entity, set),
        setCountries: setWithEntity<LineItemEntity>('countries', entity, set),
        setCustomEvents: setWithEntity<LineItemEntity>('customEvents', entity, set),
        setDco: setWithEntity<LineItemEntity>('dco', entity, set),
        setDoohAudience: (newDoohAudience: DoohAudience) => set({ doohAudience: newDoohAudience }),
        setEndDate: setWithEntity<LineItemEntity>('endDate', entity, set),
        setGoal: (newGoal: LineItemGoal) => {
          // Only one non-budget goal is allowed per timeFrame, so switching e.g. daily
          // clicks -> daily impressions must replace the previous goal, not add to it.
          const newGoals = structuredClone(get().goals || []).filter(({ timeFrame, type }) => {
            if (timeFrame !== newGoal.timeFrame) {
              return true;
            }
            if (type === 'budget' || newGoal.type === 'budget') {
              return type !== newGoal.type;
            }
            return false;
          });
          if (newGoal.target) {
            newGoals.push(newGoal);
          }
          entity.set('goals', newGoals);
          set({ goals: newGoals });
        },
        setHasUnsavedMapChanges: (hasUnsavedMapChanges: boolean) => set({ hasUnsavedMapChanges }),
        setLiName: setWithEntity<LineItemEntity>('name', entity, set),
        setMediaType: setWithEntity<LineItemEntity>('mediaType', entity, set),
        setOptimizationModel: setWithEntity<LineItemEntity>('optimizationModel', entity, set),
        setPendingSaveSteps: pendingSaveSteps => set({ pendingSaveSteps }),
        setPixels: setWithEntity<LineItemEntity>('pixels', entity, set),
        setPricingModelConfig: setWithEntity<LineItemEntity>('pricingModelConfig', entity, set),
        setSaveStep: (step, status, counts) =>
          set(state => ({
            saveSteps: { ...state.saveSteps, [step]: { counts: counts ?? state.saveSteps[step].counts, status } }
          })),
        setScheduleParting: setWithEntity<LineItemEntity>('scheduleParting', entity, set),
        setScreenGroups: setWithEntity<LineItemEntity, 'screenGroups'>('screenGroups', entity, set),
        setTargeting: setWithEntity<LineItemEntity>('targeting', entity, set),
        setTimezone: setWithEntity<LineItemEntity>('timezone', entity, set),
        setUserFrequencyCapping: setWithEntity<LineItemEntity>('userFrequencyCapping', entity, set),
        setVenues: (venues: Venue<string>[]) => {
          set({ venues });
          const ads = get().ads;
          const adsWithFilteredVenues = ads.map((ad: AdInterface<string>) => ({
            ...ad,
            adOptions: {
              ...ad.adOptions,
              // Avoid having ads with venues that have been removed of the IO
              shops:
                ad.adOptions?.shops?.filter(v =>
                  venues.some(venue => venue._id?.toString() === (v as unknown as string))
                ) || []
            }
          }));
          set({ ads: adsWithFilteredVenues as typeof ads });
        },
        setZoneGroups: setWithEntity<LineItemEntity, 'zoneGroups'>('zoneGroups', entity, set),
        shouldMapSave: false,
        user: user.get('id'),
        venues: []
      };
    }
  );

export {
  createStore,
  getHasUnsavedChanges,
  getHasUnsavedDoohAudienceChanges,
  getHasUnsavedEntityChanges,
  MAIN_SETTINGS_FIELDS,
  Provider,
  useStore
};
